import {
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  type FitBounds,
  type OnEdgesChange,
  type OnNodesChange,
  ReactFlow,
  useReactFlow,
  useStoreApi,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useAutoPersistWorkflow } from './useAutoPersistWorkflow';
import { useUndoRedo } from './useUndoRedo';
import { useHoldWhileClosing } from './useHoldWhileClosing';
import { LBNodeDisplay } from './nodes/LBNodeDisplay';
import { LBEdgeDisplay } from './nodes/LBEdgeDisplay';
import {
  CANVAS_HEIGHT,
  DEFAULT_ZOOM_LEVEL,
  FIT_VIEW_PADDING,
  LBEDGE_MARKER_END,
  LOADING_LABEL_STYLE,
  LOADING_SPINNER_SIZE,
  LOADING_STACK_STYLE,
  NODE_ORIGIN,
  RUN_BANNER_MAX_WIDTH,
  RUN_BANNER_TOP,
  RUN_BANNER_Z_INDEX,
} from './styles';
import { LBNodeUpdateProvider } from './nodes/LBNodeUpdateContext';
import type {
  ConditionalData,
  LBNodeData,
} from '@/proto/logiblocks/logiblocks';
import { LBCanvasControls } from './LBCanvasControls';
import type { LBCanvasTab } from './LBCanvasControls';
import type {
  WorkflowDetail,
  WorkflowGraphWriteResult,
} from '@/apis/logiblocksAPI';
import {
  bytesEqual,
  lbNodeDataToBytes,
  lbWorkflowToReactFlow,
} from './serialization';
import {
  createNode,
  deleteNodes,
  findTransferEscalationChild,
  insertNewChild,
  isTransferEscalationMirror,
  reconcileTransferEscalationChild,
} from './graphUtils';
import { calculateGraphLayout } from './graphLayout';
import {
  CustomizeLBNodePanel,
  type CustomizeLBNodePanelHandle,
} from './CustomizeLBNodePanel';
import { CopilotButton } from './CopilotButton';
import { WorkflowAgentChatPanel } from './agent/WorkflowAgentChatPanel';
import {
  getPlanRevealViewport,
  planRevealStepDelayMs,
  shouldUseReactFlowAutoFit,
  usePlanReveal,
} from './agent/usePlanReveal';
import agentRevealStyles from './agent/agentReveal.module.css';
import canvasStyles from './LBCanvas.module.css';
import {
  getNodeInfoForType,
  NodeType,
} from '@/pages/Logiblocks/nodes/registry';
import {
  type FilterControlFlowField,
  getFilterControlFlowFields,
} from '@/apis/logiblocksAPI';
import { getLBNodeInfo, getLBNodeType } from './nodes/registry';
import {
  computeValidationErrors,
  getUnresolvedFieldSummary,
  isUnconfiguredDefaultNode,
  type ValidationError,
} from './validation';
import { InitialTriggerSelector } from './InitialTriggerSelector';
import { NodePickerOverlay } from './nodes/NodePickerOverlay';
import type {
  LBNodeDataWithIndexer,
  ReactFlowEdge,
  ReactFlowNode,
} from '@/pages/Logiblocks/types';
import { WorkflowRunHistoryPanel } from './runHistory/WorkflowRunHistoryPanel';
import { useDelayedFlag } from './runHistory/useDelayedFlag';
import { useWorkflowRunDetail } from './runHistory/useWorkflowRunDetail';
import { RunNodeAttemptsDrawer } from './runHistory/RunNodeAttemptsDrawer';
import { useRunGraphSnapshot } from './runHistory/useRunGraphSnapshot';
import {
  buildRunNodeStatusMap,
  getNodeRingVariant,
  getRunEdgeColor,
  getRunGraphSource,
  getRunGraphStatus,
} from './runVisualization';
import { Alert, message, Spin } from 'antd';
import { Lock } from 'lucide-react';
import { useToken } from '@/common/helper';
import { AutomationsEmptyState } from './logiblockDashboard/components/AutomationsEmptyState';
import {
  LogiblocksGate,
  useLogiblocksGateEnabled,
} from './useLogiblocksGateEnabled';
import { useTestRunPanel } from './testRun/TestRunPanel';
import { useMenuPermissions } from '@/hooks';
import { MenuLabels } from '@/common/enum';
import { listAgentSessions } from '@/apis/logiblocksAgentAPI';

const ignoreConfigurationLifecycleChange = (): void => undefined;

/** The run a link asks to open (`...&run=<run id>`, see AppRoutes.LOGIBLOCKS_RUN), if any. */
const runIdFromUrl = (): string | undefined =>
  new URLSearchParams(window.location.search).get('run') || undefined;

/**
 * Exposes ReactFlow's `fitView`/`fitBounds` functions via refs, for callers
 * outside the ReactFlow tree (useReactFlow can only be called from a
 * component rendered as ReactFlow's child). fitBounds is what
 * usePlanReveal's camera move uses -- unlike fitView, it takes a
 * caller-supplied rectangle and never depends on the live node lookup, so
 * it can frame nodes that aren't mounted on canvas yet.
 */
function FitViewRefSetter({
  fitViewRef,
  fitBoundsRef,
}: {
  fitViewRef: React.MutableRefObject<(() => void) | null>;
  fitBoundsRef: React.MutableRefObject<FitBounds | null>;
}): null {
  const { fitView, setViewport } = useReactFlow();
  const storeApi = useStoreApi();

  useEffect(() => {
    fitViewRef.current = () => {
      const _ = fitView({
        maxZoom: DEFAULT_ZOOM_LEVEL,
        padding: FIT_VIEW_PADDING,
      });
    };
  }, [fitView, fitViewRef]);

  useEffect(() => {
    fitBoundsRef.current = (bounds, options = {}) => {
      const { width, height, minZoom, maxZoom } = storeApi.getState();
      const { padding = FIT_VIEW_PADDING, ...viewportOptions } = options;
      return setViewport(
        getPlanRevealViewport(bounds, width, height, minZoom, maxZoom, padding),
        viewportOptions
      );
    };
  }, [fitBoundsRef, setViewport, storeApi]);

  return null;
}

/** True if keyboard event is an undo shortcut (Ctrl/Cmd+Z) */
function isUndoKeydownEvent(event: KeyboardEvent): boolean {
  return (
    (event.ctrlKey || event.metaKey) && event.key === 'z' && !event.shiftKey
  );
}

/** True if keyboard event is a redo shortcut (Ctrl/Cmd+Shift+Z) */
function isRedoKeydownEvent(event: KeyboardEvent): boolean {
  return (
    (event.ctrlKey || event.metaKey) && event.key === 'z' && event.shiftKey
  );
}

// Banners about the selected run share one slot above the graph. Only one of
// them shows at a time: a run that failed to load leaves nothing to call stale.
const RUN_BANNER_STYLE: CSSProperties = {
  position: 'absolute',
  top: RUN_BANNER_TOP,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: RUN_BANNER_Z_INDEX,
  maxWidth: RUN_BANNER_MAX_WIDTH,
};

// The old-run warning reads as one line, so it sizes to its text instead of
// wrapping inside the shared max width.
const RUN_NOTICE_BANNER_STYLE: CSSProperties = {
  ...RUN_BANNER_STYLE,
  maxWidth: 'none',
  whiteSpace: 'nowrap',
};

// A run selection loads two chained fetches before its graph can be painted. A
// spinner is only worth showing past this wait, so a fast or cached load does
// not flash one.
const RUN_GRAPH_LOADING_DELAY_MS = 250;
const NODE_VALUE_REVEAL_DURATION_MS = 360;

// Covers the canvas but sits below the run-history panel, and takes no clicks so
// the canvas underneath stays interactive.
const RUN_LOADING_OVERLAY_STYLE: CSSProperties = {
  ...LOADING_STACK_STYLE,
  position: 'absolute',
  inset: 0,
  pointerEvents: 'none',
  zIndex: RUN_BANNER_Z_INDEX,
};

// Shown for a selected run whose graph cannot be shown, rather than painting its
// outcome onto the live graph. A stable reference so the display-graph memo and
// its consumers don't rerun on every render.
const EMPTY_GRAPH: { nodes: ReactFlowNode[]; edges: ReactFlowEdge[] } = {
  nodes: [],
  edges: [],
};

type NodeValueReveal = {
  delayByNodeId: ReadonlyMap<string, number>;
  generation: number;
};

// Which notice floats over a selected run's graph, or null for none: 'older' is
// informational (the shown snapshot is not the current workflow); 'missing' and
// 'unavailable' both mean no graph can ever be shown and share one warning;
// 'load-failed' means the graph exists but could not be loaded this time.
type RunGraphNotice =
  'older' | 'missing' | 'unavailable' | 'load-failed' | null;

interface LBCanvasProps {
  /** The workflow to display and edit */
  workflow: WorkflowDetail;
  /** Callback fired when the back button is clicked */
  onBack?: () => void;
  /**
   * Persist the current graph to the backend. Called automatically by the
   * auto-persist hook (debounced on user edits, force-flushed on unload).
   * Pass keepalive=true through to fetch when invoked during page unload.
   */
  onPersistWorkflow: (
    nodes: ReactFlowNode[],
    edges: ReactFlowEdge[],
    opts?: { keepalive?: boolean }
  ) => Promise<WorkflowGraphWriteResult>;
  /** Callback fired when the workflow name is changed */
  onChangeWorkflowName?: (workflowId: string, newName: string) => void;
  /** Callback fired when the publish button is clicked (includes current graph state) */
  onPublishWorkflow?: (
    workflowId: string,
    nodes: ReactFlowNode[],
    edges: ReactFlowEdge[]
  ) => void;
  /** Callback fired when the unpublish toggle is clicked */
  onUnpublishWorkflow?: (workflowId: string) => void;
  /** Callback fired when the delete button is clicked */
  onDeleteWorkflow?: (workflowId: string) => void;
  /**
   * Description to auto-send as the AI Builder's first message (from the
   * Automations empty-state's Generate/template flow). When present, the
   * AI Builder panel opens automatically on mount instead of waiting for
   * the user to click the toolbar toggle.
   */
  initialAgentPrompt?: string;
}

/**
 * Root component for Logiblocks workflow editing/viewing.
 * - Renders the ReactFlow workflow graph
 * - If this is a brand new graph, show graph initialization options instead
 * - Owns all graph data. Handles node mutations, layout, undo/redo
 */
export function LBCanvas({
  workflow,
  onBack,
  onPersistWorkflow,
  onChangeWorkflowName,
  onPublishWorkflow,
  onUnpublishWorkflow,
  onDeleteWorkflow,
  initialAgentPrompt,
}: LBCanvasProps) {
  // A link to one run (the "Link to run" in a notification) opens the canvas on that run: the
  // Runs tab with that run selected, in place of the editor. Read once, when the canvas mounts.
  const [initialRunId] = useState(runIdFromUrl);
  // Published workflows are read-only (except for name changes and unpublishing)
  const isReadOnly = !workflow.is_draft;
  const [activeTab, setActiveTab] = useState<LBCanvasTab>(
    initialRunId ? 'runs' : 'editor'
  );
  const isHistoryMode = activeTab === 'runs';
  const isCopilotEnabled = useLogiblocksGateEnabled(LogiblocksGate.AI_BUILDER);
  const { token } = useToken();
  const [draftLockReason, setDraftLockReason] = useState<
    'generating' | 'revealing' | 'awaiting_answer' | null
  >(null);
  const isDrafting = draftLockReason !== null;
  const [hasCopilotRecovery, setHasCopilotRecovery] = useState(false);

  // Initialize nodes and edges from the workflow, then apply layout
  const initialGraph = useMemo(() => {
    const reactFlowGraph = lbWorkflowToReactFlow(workflow.workflow);
    return {
      nodes: calculateGraphLayout(reactFlowGraph.nodes, reactFlowGraph.edges),
      edges: reactFlowGraph.edges,
    };
  }, [workflow.workflow]);

  const [nodes, setNodes] = useState<ReactFlowNode[]>(initialGraph.nodes);
  const [edges, setEdges] = useState<ReactFlowEdge[]>(initialGraph.edges);
  const [nodeValueReveal, setNodeValueReveal] = useState<NodeValueReveal>({
    delayByNodeId: new Map(),
    generation: 0,
  });
  const nodeValueRevealTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (nodeValueRevealTimerRef.current !== null) {
        window.clearTimeout(nodeValueRevealTimerRef.current);
      }
    };
  }, []);

  // Auto-persist graph changes to the backend. The hook owns debounce, in-flight
  // coordination, unmount/unload flushes, and surfaces save status for UI.
  const {
    status: autoPersistStatus,
    lastSavedAt: autoPersistLastSavedAt,
    flushSave: flushWorkflowSave,
    adoptAuthoritativeSnapshot,
  } = useAutoPersistWorkflow({
    workflowId: workflow.id,
    nodes,
    edges,
    onPersist: onPersistWorkflow,
    enabled: workflow.is_draft,
    paused: isDrafting,
  });

  // Validation errors by nodeID
  const [validationErrors, setValidationErrors] = useState<
    Map<string, ValidationError[]>
  >(new Map());

  // Recovery discovery remains available even when the rollout gate is off.
  // This is intentionally a read-only status request: it does not authorize
  // new model work, but prevents an already-started v2 draft from becoming
  // unreachable after a gate change or rollback.
  useEffect(() => {
    if (isCopilotEnabled || !workflow.is_draft || !workflow.id || !token) {
      setHasCopilotRecovery(false);
      return;
    }
    const controller = new AbortController();
    void listAgentSessions(token, workflow.id, controller.signal)
      .then((sessions) => {
        setHasCopilotRecovery(
          sessions.some(
            (session) => session.draft_status?.draft_protocol_version === 2
          )
        );
      })
      .catch(() => setHasCopilotRecovery(false));
    return () => controller.abort();
  }, [isCopilotEnabled, workflow.is_draft, workflow.id, token]);

  // The run whose outcome is painted onto the graph. Owned here so both the
  // Runs panel and the graph agree on one selection. Only meaningful in Runs
  // mode, so the run fetch is gated on it.
  const [selectedRunId, setSelectedRunId] = useState<string | null>(
    initialRunId ?? null
  );
  // The Runs panel's `key`, changed when a test run starts so the panel remounts and fetches its
  // run list again. Without it, a panel already showing doesn't list the new run, and it selects
  // its newest listed run in place of the new one.
  const [runHistoryPanelKey, setRunHistoryPanelKey] = useState(0);
  const { runDetail, runDetailError, runStatusChangeCount, refetchRunDetail } =
    useWorkflowRunDetail(workflow.id, isHistoryMode ? selectedRunId : null);
  const runNodeStatuses = useMemo(
    () => (runDetail === null ? null : buildRunNodeStatusMap(runDetail.nodes)),
    [runDetail]
  );

  // The node whose per-attempt audit trail is open in Runs mode. A loop
  // node's iterations each get their own WorkflowRunNodeDetail row sharing
  // one node_id, so this reads straight off runDetail.nodes rather than the
  // one-status-per-id runNodeStatuses map above.
  const [runNodeDetailId, setRunNodeDetailId] = useState<string | null>(null);
  const handleOpenRunNodeDetail = useCallback((nodeId: string) => {
    setRunNodeDetailId(nodeId);
  }, []);
  const runNodeDetailRows = useMemo(() => {
    if (runNodeDetailId === null || runDetail === null) {
      return [];
    }
    return runDetail.nodes.filter((node) => node.node_id === runNodeDetailId);
  }, [runNodeDetailId, runDetail]);
  // Closing the drawer when the selected run changes (or Runs mode exits)
  // avoids showing a stale node's attempts under a new run.
  useEffect(() => {
    setRunNodeDetailId(null);
  }, [isHistoryMode, selectedRunId]);

  // The graph that actually ran, fetched from the selected run's published
  // version, or from a test run's stored graph, and cached across selections.
  // Null when no run is selected or when the run predates version tracking and
  // so has no snapshot to show.
  const runGraphSource =
    runDetail === null ? null : getRunGraphSource(runDetail);
  const { snapshot: runGraphSnapshot, status: snapshotStatus } =
    useRunGraphSnapshot(workflow.id, runGraphSource);

  // Undo/redo state
  const { saveCheckpoint, undo, redo, clearHistory, canUndo, canRedo } =
    useUndoRedo();

  // Clear undo/redo history when switching workflows
  useEffect(() => {
    clearHistory();
    if (nodeValueRevealTimerRef.current !== null) {
      window.clearTimeout(nodeValueRevealTimerRef.current);
      nodeValueRevealTimerRef.current = null;
    }
    setNodeValueReveal((current) => ({
      delayByNodeId: new Map(),
      generation: current.generation,
    }));
  }, [workflow.id, clearHistory]);

  // The selected run's snapshot as React Flow nodes and edges, laid out the way the live
  // graph would be. Null while there is no snapshot, or when the snapshot can't be turned
  // into a graph.
  // React Flow hides a node until it measures the node's element, and it treats each new node
  // object as unmeasured. A React Flow bug: if new objects arrive just after a measure, it hides
  // the nodes again and never re-measures them, because the element's size hasn't changed.
  // Selecting a run changes `selectedRunId`, `runDetail` and `runGraphSnapshot` in separate
  // renders. Depending only on `runGraphSnapshot` keeps the same node objects through the
  // first two, so React Flow gets one new set per selection.
  const runGraph = useMemo((): {
    nodes: ReactFlowNode[];
    edges: ReactFlowEdge[];
  } | null => {
    if (runGraphSnapshot === null) {
      return null;
    }
    try {
      const reactFlowGraph = lbWorkflowToReactFlow(runGraphSnapshot);
      return {
        nodes: calculateGraphLayout(reactFlowGraph.nodes, reactFlowGraph.edges),
        edges: reactFlowGraph.edges,
      };
    } catch (error) {
      // A snapshot that uses a node type since removed cannot be turned into a graph.
      console.error('Failed to render run graph snapshot:', error);
      return null;
    }
  }, [runGraphSnapshot]);

  // The graph to render and any notice to float over it. A selected run shows
  // only the exact graph it executed (its snapshot). When that snapshot cannot
  // be shown, the run shows nothing and a notice, rather than paint its outcome
  // onto the live graph, which may have moved on: 'missing' for a run predating
  // version tracking, 'unavailable' when the bytes cannot be turned into a
  // graph (e.g. they use a node type since removed), 'load-failed' when the
  // fetch itself failed and retrying may succeed, 'older' when the snapshot is
  // shown but is not the current workflow. A test run's stored graph is shown
  // with no notice. With no run selected, the live graph being edited is shown.
  const { displayGraph, runNotice } = useMemo((): {
    displayGraph: { nodes: ReactFlowNode[]; edges: ReactFlowEdge[] };
    runNotice: RunGraphNotice;
  } => {
    if (!isHistoryMode || selectedRunId === null || runDetail === null) {
      return { displayGraph: { nodes, edges }, runNotice: null };
    }
    const graphStatus = getRunGraphStatus(runDetail, workflow.is_draft);
    if (graphStatus === 'missing') {
      return { displayGraph: EMPTY_GRAPH, runNotice: 'missing' };
    }
    if (snapshotStatus === 'unavailable') {
      return { displayGraph: EMPTY_GRAPH, runNotice: 'unavailable' };
    }
    if (snapshotStatus === 'load-failed') {
      return { displayGraph: EMPTY_GRAPH, runNotice: 'load-failed' };
    }
    if (runGraphSnapshot === null) {
      // Snapshot still loading: show nothing yet, no notice.
      return { displayGraph: EMPTY_GRAPH, runNotice: null };
    }
    if (runGraph === null) {
      return { displayGraph: EMPTY_GRAPH, runNotice: 'unavailable' };
    }
    return {
      displayGraph: runGraph,
      runNotice: graphStatus === 'older' ? 'older' : null,
    };
  }, [
    isHistoryMode,
    selectedRunId,
    runDetail,
    runGraphSnapshot,
    runGraph,
    snapshotStatus,
    nodes,
    edges,
    workflow.is_draft,
  ]);

  // The type of the node whose attempts drawer is open, read from the graph the run executed.
  const runNodeDetailType = useMemo((): NodeType | null => {
    if (runNodeDetailId === null) {
      return null;
    }
    const node = displayGraph.nodes.find((n) => n.id === runNodeDetailId);
    if (node === undefined) {
      return null;
    }
    return getLBNodeType(node.data) ?? null;
  }, [runNodeDetailId, displayGraph]);

  // The Conditional data of the node whose attempts drawer is open, read from the graph the run
  // executed. Null if the node isn't a Conditional.
  const runNodeDetailConditional = useMemo((): ConditionalData | null => {
    if (runNodeDetailId === null) {
      return null;
    }
    const node = displayGraph.nodes.find((n) => n.id === runNodeDetailId);
    if (node === undefined) {
      return null;
    }
    return node.data.conditional ?? null;
  }, [runNodeDetailId, displayGraph]);

  // Whether the selected run's graph is still loading: its detail is not yet the
  // current run's, or its snapshot has neither loaded nor settled on a notice.
  // This mirrors the display memo's silent empty-canvas branch, so the spinner
  // shows exactly when the canvas would otherwise sit blank. An error resolves
  // it, since a notice takes over from there.
  const isRunGraphLoading =
    isHistoryMode &&
    selectedRunId !== null &&
    runDetailError === null &&
    !(
      runDetail?.id === selectedRunId &&
      (runNotice !== null || runGraphSnapshot !== null)
    );
  const showRunGraphLoading = useDelayedFlag(
    isRunGraphLoading,
    RUN_GRAPH_LOADING_DELAY_MS,
    selectedRunId
  );

  // Colour each edge by the run status of the node it points at.
  // Display-only: neither the stroke nor the arrowhead ever reach the
  // proto, and outside Runs this is the untouched display graph edges.
  const displayEdges = useMemo(() => {
    if (runNodeStatuses === null) {
      return displayGraph.edges;
    }
    return displayGraph.edges.map((edge) => {
      const color = getRunEdgeColor(
        getNodeRingVariant(runNodeStatuses.get(edge.target))
      );
      return {
        ...edge,
        style: { ...edge.style, stroke: color },
        markerEnd: { ...LBEDGE_MARKER_END, color },
      };
    });
  }, [displayGraph.edges, runNodeStatuses]);

  // Clear node selection when entering Runs: a selected node's blue outline
  // would read as run status. ReactFlow can't clear it once we're there,
  // because onNodesChange ignores changes in run mode.
  useEffect(() => {
    if (!isHistoryMode) {
      return;
    }
    setNodes((current) =>
      current.some((node) => node.selected)
        ? current.map((node) =>
            node.selected ? { ...node, selected: false } : node
          )
        : current
    );
  }, [isHistoryMode]);

  // Customize panel state
  const [isCustomizePanelOpen, setIsCustomizePanelOpen] = useState(false);
  const [customizePanelNodeId, setCustomizePanelNodeId] = useState<
    string | null
  >(null);
  // Lets handleOpenCustomizePanel ask the currently-open panel to run its
  // own dirty-draft check before switching to a different node.
  const customizePanelRef = useRef<CustomizeLBNodePanelHandle>(null);
  // Fields pre-fetched for filterControlFlow panels only. Conditional panels
  // fetch their own field list lazily inside ConditionalCustomization.
  const [panelPrefetchedFields, setPanelPrefetchedFields] = useState<
    FilterControlFlowField[] | undefined
  >(undefined);
  // The parent node ID resolved at panel-open time; forwarded to the
  // customisation component for paginated API calls (filterControlFlow) and
  // for the conditional component's own lazy field fetch.
  const [panelParentId, setPanelParentId] = useState<string | undefined>(
    undefined
  );

  // AI Builder chat panel state. Opens automatically when arriving with a
  // prompt to auto-send (Generate/template flow from the empty state).
  const [isAgentPanelOpen, setIsAgentPanelOpen] = useState(() =>
    Boolean(initialAgentPrompt)
  );
  const effectiveReadOnly = isReadOnly || isDrafting;

  // Description typed into the on-canvas "Describe the automation you want"
  // card (see the AutomationsEmptyState overlay below), to auto-send once
  // the agent panel opens. Kept separate from `initialAgentPrompt` -- that
  // prop is a one-time mount-time value from the caller, while this can be
  // set after the canvas has already mounted with an empty draft.
  const [canvasAgentPrompt, setCanvasAgentPrompt] = useState<
    string | undefined
  >(undefined);

  // Once the user picks "Build from scratch" off the on-canvas Describe
  // card, fall through to the trigger picker instead -- same destination as
  // the Copilot-off path, just reached by an extra click instead of
  // skipping the card entirely.
  const [showTriggerPicker, setShowTriggerPicker] = useState(false);

  // Handles Generate from the on-canvas Describe card. The chat session
  // needs a real (persisted) workflow id before it can start (see the
  // `!workflowId` guard in WorkflowAgentChatPanel) -- a brand-new draft from
  // "Create new" hasn't been saved yet, so persist it up front instead of
  // waiting on the debounced auto-persist.
  const handleCanvasGenerate = useCallback(
    (description: string) => {
      setCanvasAgentPrompt(description);
      setIsAgentPanelOpen(true);
      if (!workflow.id) {
        onPersistWorkflow([], []).catch((error) => {
          message.error(
            error instanceof Error
              ? error.message
              : 'Failed to start the AI Builder.'
          );
        });
      }
    },
    [workflow.id, onPersistWorkflow]
  );

  // Closes the Copilot panel when leaving the Editor tab. The panel's own
  // mount condition (workflow.is_draft) has no activeTab gate, and on a
  // narrow (<=900px) viewport WorkflowRunHistoryPanel's own responsive
  // override becomes left-anchored + full-width at the same z-index as the
  // chat panel -- without this, an already-open Copilot panel would stay
  // mounted underneath it on the Runs tab.
  const handleTabChange = useCallback((tab: LBCanvasTab) => {
    if (tab !== 'editor') {
      setIsAgentPanelOpen(false);
    }
    setActiveTab(tab);
  }, []);

  const startNodeValueReveal = useCallback(
    (
      previousNodes: ReactFlowNode[],
      nextNodes: ReactFlowNode[],
      requestedNodeIds: string[]
    ) => {
      if (requestedNodeIds.length === 0) return;
      const previousById = new Map(
        previousNodes.map((node) => [node.id, node.data])
      );
      const nextById = new Map(nextNodes.map((node) => [node.id, node.data]));
      const changedNodeIds = [...new Set(requestedNodeIds)].filter((nodeId) => {
        const previousData = previousById.get(nodeId);
        const nextData = nextById.get(nodeId);
        return Boolean(
          previousData &&
          nextData &&
          !bytesEqual(
            lbNodeDataToBytes(previousData),
            lbNodeDataToBytes(nextData)
          )
        );
      });
      if (changedNodeIds.length === 0) return;

      const stepDelay = planRevealStepDelayMs(changedNodeIds.length);
      const delayByNodeId = new Map(
        changedNodeIds.map((nodeId, index) => [nodeId, index * stepDelay])
      );
      if (nodeValueRevealTimerRef.current !== null) {
        window.clearTimeout(nodeValueRevealTimerRef.current);
      }
      setNodeValueReveal((current) => ({
        delayByNodeId,
        generation: current.generation + 1,
      }));
      nodeValueRevealTimerRef.current = window.setTimeout(
        () => {
          setNodeValueReveal((current) => ({
            delayByNodeId: new Map(),
            generation: current.generation,
          }));
          nodeValueRevealTimerRef.current = null;
        },
        (changedNodeIds.length - 1) * stepDelay + NODE_VALUE_REVEAL_DURATION_MS
      );
    },
    []
  );

  // Applies an AI Builder plan (already Gate-2 validated by the caller)
  // directly to the canvas -- same as any manual edit, no review step. One
  // checkpoint per plan, same discipline as every other bulk graph
  // mutation in this file.
  const handleApplyPlan = useCallback(
    (
      newNodes: ReactFlowNode[],
      newEdges: ReactFlowEdge[],
      presentation?: { animateNodeIds?: string[] }
    ) => {
      saveCheckpoint(nodes, edges);
      // Start the transient class before the first setNodes that contains the
      // configured value. Attaching it later at receipt reconciliation lets
      // the browser paint the final subtitle once with no animation.
      startNodeValueReveal(nodes, newNodes, presentation?.animateNodeIds ?? []);
      // Nodes applyGraphOps just added come straight from createNode(),
      // which always stamps position {x: 0, y: 0} -- calculateGraphLayout
      // is what turns that into real, non-overlapping coordinates. Every
      // other mutation path here already goes through it before setNodes;
      // skipping it made newly-added nodes render stacked exactly on top of
      // their parent (looking like the parent got replaced) until the next
      // full reload recomputed layout from scratch.
      setNodes(calculateGraphLayout(newNodes, newEdges));
      setEdges(newEdges);
      fitViewRef.current?.();
    },
    [nodes, edges, saveCheckpoint, startNodeValueReveal]
  );

  // NodePicker state (for adding child nodes)
  const [nodePickerParentNodeId, setNodePickerParentNodeId] = useState<
    string | null
  >(null);
  const [nodePickerButtonRect, setNodePickerButtonRect] =
    useState<DOMRect | null>(null);

  // Stable refs so the async panel-open handler can read the latest
  // nodes/edges without those values appearing in its useCallback dep array.
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const edgesRef = useRef(edges);
  edgesRef.current = edges;
  const workflowIdRef = useRef(workflow.id);
  workflowIdRef.current = workflow.id;

  // Ref to ReactFlow's fitView function (set by FitViewRefSetter inside ReactFlow)
  const fitViewRef = useRef<(() => void) | null>(null);
  // Ref to ReactFlow's fitBounds function, for usePlanReveal's camera move --
  // fitBounds (unlike fitView) takes a caller-supplied rectangle and never
  // depends on the live node lookup, so it can frame nodes not yet mounted.
  const fitBoundsRef = useRef<FitBounds | null>(null);
  // Ref to the chat panel's own root DOM node, so usePlanReveal can measure
  // its real rendered width (the panel's CSS is `min(420px, 46vw)`, so a
  // hardcoded guess would be wrong below ~913px) at the moment it moves the
  // camera, rather than a stale or hardcoded number.
  const agentPanelRef = useRef<HTMLDivElement | null>(null);

  const planReveal = usePlanReveal({
    nodes,
    edges,
    setNodes,
    setEdges,
    saveCheckpoint,
    isAgentPanelOpen,
    getAgentPanelWidth: () =>
      agentPanelRef.current?.getBoundingClientRect().width ?? 0,
    fitBoundsRef,
  });

  const handleReconcileSegmentGraph = useCallback(
    (
      authoritativeNodes: ReactFlowNode[],
      authoritativeEdges: ReactFlowEdge[],
      presentation?: { animateNodeIds?: string[] }
    ) => {
      startNodeValueReveal(
        nodesRef.current,
        authoritativeNodes,
        presentation?.animateNodeIds ?? []
      );
      adoptAuthoritativeSnapshot(authoritativeNodes, authoritativeEdges);
      setNodes(calculateGraphLayout(authoritativeNodes, authoritativeEdges));
      setEdges(authoritativeEdges);
      fitViewRef.current?.();
    },
    [adoptAuthoritativeSnapshot, startNodeValueReveal]
  );

  // Restores a Copilot checkpoint by id, for the chat panel's Revert
  // button. `nodes`/`edges` are already deserialized by getCheckpoint --
  // deserializing the checkpoint's structural bytes is that function's
  // job, not this one's. `positions` is overlaid onto them with no
  // calculateGraphLayout() call: the deserialized nodes carry the
  // placeholder (0, 0) position every serialize/deserialize round-trip
  // produces (the protobuf schema never stores position), but re-laying-out
  // a checkpoint's partial subgraph in isolation wouldn't reproduce the
  // positions it had as a fragment of the full final-graph layout either --
  // only the checkpoint's own stored positions do that.
  const handleRestoreAgentSnapshot = useCallback(
    (
      restoredNodes: ReactFlowNode[],
      restoredEdges: ReactFlowEdge[],
      positions: Map<string, { x: number; y: number }>
    ) => {
      if (isDrafting) return;
      planReveal.cancel({ flushToFinal: false });
      saveCheckpoint(nodes, edges);
      const withFrozenPositions = restoredNodes.map((node) => {
        const position = positions.get(node.id);
        return position ? { ...node, position } : node;
      });
      setNodes(withFrozenPositions);
      setEdges(restoredEdges);
      fitViewRef.current?.();
    },
    [isDrafting, nodes, edges, saveCheckpoint, planReveal]
  );

  // Recompute validation errors whenever nodes or edges change, but not
  // mid-reveal: an intermediate step's graph (a paths node with only its
  // first branch built, an unconfigured conditional) is a real intermediate
  // state, not a mistake -- surfacing its errors would flash validation
  // noise the user never asked about. isRevealing is listed as a dependency,
  // not just checked in the body, so this effect reruns on the tick it
  // flips back to false (nodes/edges typically stop changing one tick
  // earlier) and the just-finished reveal's real errors appear.
  useEffect(() => {
    if (planReveal.isRevealing) return;
    const errors = computeValidationErrors({ nodes, edges });
    setValidationErrors(errors);
  }, [nodes, edges, planReveal.isRevealing]);

  const presentedValidationErrors = useMemo(() => {
    // Exclude nodes still exactly their type's fresh default: a red ring / a
    // blocked publish toggle before the author has touched the node would be
    // a false alarm, not a real problem. Presentation-only - the raw
    // validationErrors map (and the backend's own publish-time check) are
    // untouched, so an unconfigured node still can't actually be published.
    const filtered = new Map<string, ValidationError[]>();
    validationErrors.forEach((errors, nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (node && isUnconfiguredDefaultNode(node.data as LBNodeData)) return;
      filtered.set(nodeId, errors);
    });
    return filtered;
  }, [validationErrors, nodes]);

  const unresolvedFieldSummary = useMemo(
    () => getUnresolvedFieldSummary(presentedValidationErrors),
    [presentedValidationErrors]
  );

  // Applies the CSS enter animation's transient class to whatever the
  // current reveal step just added. A target node is first mounted with the
  // staged class so React Flow can draw its incoming edge, then swaps to the
  // entering class. These classes never touch node.data, which the frozen
  // protobuf payload autopersist/undo/diffing depend on.
  const revealNodes = useMemo(() => {
    if (
      (!planReveal.isRevealing ||
        (planReveal.enteringNodeIds.size === 0 &&
          planReveal.stagedNodeIds.size === 0)) &&
      nodeValueReveal.delayByNodeId.size === 0
    ) {
      return displayGraph.nodes;
    }
    return displayGraph.nodes.map((node) => {
      const valueRevealDelay = nodeValueReveal.delayByNodeId.get(node.id);
      const revealClass =
        planReveal.isRevealing && planReveal.stagedNodeIds.has(node.id)
          ? agentRevealStyles.staged
          : planReveal.isRevealing && planReveal.enteringNodeIds.has(node.id)
            ? agentRevealStyles.entering
            : valueRevealDelay !== undefined
              ? nodeValueReveal.generation % 2 === 0
                ? agentRevealStyles.valueUpdatingEven
                : agentRevealStyles.valueUpdatingOdd
              : null;
      return revealClass
        ? {
            ...node,
            className: [node.className, revealClass].filter(Boolean).join(' '),
            style:
              valueRevealDelay === undefined
                ? node.style
                : ({
                    ...node.style,
                    '--agent-node-value-reveal-delay': `${valueRevealDelay}ms`,
                  } as CSSProperties),
          }
        : node;
    });
  }, [
    displayGraph.nodes,
    planReveal.isRevealing,
    planReveal.enteringNodeIds,
    planReveal.stagedNodeIds,
    nodeValueReveal,
  ]);

  const revealEdges = useMemo(() => {
    if (!planReveal.isRevealing || planReveal.enteringEdgeIds.size === 0) {
      return displayEdges;
    }
    return displayEdges.map((edge) =>
      planReveal.enteringEdgeIds.has(edge.id)
        ? {
            ...edge,
            className: [edge.className, agentRevealStyles.entering]
              .filter(Boolean)
              .join(' '),
          }
        : edge
    );
  }, [displayEdges, planReveal.isRevealing, planReveal.enteringEdgeIds]);

  /**
   * Fired by ReactFlow when something mutates the nodes array — selection,
   * drag, add, remove. Our job is to filter that stream of changes based on
   * the current mode (read-only, panel-open) and apply the survivors to the
   * canonical nodes state.
   */
  const onNodesChange: OnNodesChange = useCallback(
    (changes) => {
      if (isDrafting) return;
      // While the customize panel is open, the user is focused on editing one
      // node — ignore selection changes (and everything else) from the
      // canvas.
      if (isCustomizePanelOpen || isHistoryMode) {
        return;
      }
      // applyNodeChanges loses xyflow's data generic, so cast back.
      if (isReadOnly) {
        // In read-only mode, only allow selection changes (for viewing)
        const allowedChanges = changes.filter(
          (change) => change.type === 'select'
        );
        if (allowedChanges.length === 0) return;
        setNodes(
          (prev) => applyNodeChanges(allowedChanges, prev) as ReactFlowNode[]
        );
      } else {
        setNodes((prev) => applyNodeChanges(changes, prev) as ReactFlowNode[]);
      }
    },
    [isDrafting, isReadOnly, isCustomizePanelOpen, isHistoryMode]
  );

  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      if (isDrafting || isReadOnly || isHistoryMode) {
        return;
      }

      // Disallow clicking on an edge to select it
      const nonSelectChanges = changes.filter(
        (change) => change.type !== 'select'
      );
      if (nonSelectChanges.length === 0) {
        return;
      }

      setEdges((prev) => applyEdgeChanges(nonSelectChanges, prev));
    },
    [isDrafting, isReadOnly, isHistoryMode]
  );

  const handleCloseCustomizePanel = useCallback(() => {
    setIsCustomizePanelOpen(false);
    setCustomizePanelNodeId(null);
    setPanelPrefetchedFields(undefined as never);
    setPanelParentId(undefined);
  }, []);

  // The Test button shows behind its gate, to users who can edit automations.
  const isTestRunsEnabled = useLogiblocksGateEnabled(LogiblocksGate.TEST_RUNS);
  const canUpdateAutomations =
    useMenuPermissions(MenuLabels.Automations)?.is_update ?? false;

  // Opens a started test run in Runs, where the canvas draws and polls it.
  const handleTestRunStarted = useCallback(
    (runId: string) => {
      setSelectedRunId(runId);
      setRunHistoryPanelKey((key) => key + 1);
      handleTabChange('runs');
    },
    [handleTabChange]
  );
  const { openTestRunPanel, testRunPanel } = useTestRunPanel({
    workflowId: workflow.id,
    nodes,
    edges,
    onStarted: handleTestRunStarted,
  });

  /** Opens the test panel, closing the Copilot and Customize panels on the same side of the canvas. */
  function handleOpenTestRunPanel(): void {
    setIsAgentPanelOpen(false);
    if (isCustomizePanelOpen && customizePanelRef.current) {
      customizePanelRef.current.requestClose(() => {
        handleCloseCustomizePanel();
        openTestRunPanel();
      });
    } else {
      openTestRunPanel();
    }
  }

  useEffect(() => {
    if (!isDrafting) return;
    setNodePickerParentNodeId(null);
    if (isCustomizePanelOpen && customizePanelRef.current) {
      customizePanelRef.current.requestClose(handleCloseCustomizePanel);
    }
  }, [isDrafting, isCustomizePanelOpen, handleCloseCustomizePanel]);

  /** Handle undo action (used by keyboard shortcut and button) */
  const handleUndo = useCallback(() => {
    if (isDrafting) return;
    const result = undo(nodes, edges);
    if (result) {
      setNodes(calculateGraphLayout(result.nodes, result.edges));
      setEdges(result.edges);
      handleCloseCustomizePanel();
    }
  }, [isDrafting, nodes, edges, undo, handleCloseCustomizePanel]);

  /** Handle redo action (used by keyboard shortcut and button) */
  const handleRedo = useCallback(() => {
    if (isDrafting) return;
    const result = redo(nodes, edges);
    if (result) {
      setNodes(calculateGraphLayout(result.nodes, result.edges));
      setEdges(result.edges);
      handleCloseCustomizePanel();
    }
  }, [isDrafting, nodes, edges, redo, handleCloseCustomizePanel]);

  // Delete every currently-selected node and all their descendants. Returns
  // true if anything was deleted, so the keydown listener can skip
  // preventDefault when there was nothing to act on.
  const handleDeleteSelectedNodes = useCallback((): boolean => {
    if (isDrafting) return false;
    // A Transfer To node's escalation mirror is coupled to it and can't be
    // deleted on its own — deleting the transfer node still cascades to it.
    const selectedNodeIds = nodes
      .filter((node) => node.selected && !isTransferEscalationMirror(node))
      .map((node) => node.id);
    if (selectedNodeIds.length === 0) {
      return false;
    }
    saveCheckpoint(nodes, edges);
    const result = deleteNodes(selectedNodeIds, nodes, edges);
    setNodes(calculateGraphLayout(result.nodes, result.edges));
    setEdges(result.edges);
    return true;
  }, [isDrafting, nodes, edges, saveCheckpoint]);

  // Global keyboard shortcuts for Undo / Redo / Delete. These live on the
  // document (not on a React onKeyDown) so they fire regardless of where focus
  // currently sits — after the customize panel opens, focus leaves the canvas
  // and doesn't reliably return, so a canvas-scoped React handler would stop
  // working for the rest of the session.
  useEffect(() => {
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
      // Shortcuts act on graph state; suppress while the customize panel is
      // editing a draft so a stray keypress doesn't rewind or delete the
      // canvas out from under the user's session. Also suppressed for the
      // duration of a Copilot reveal -- usePlanReveal is mid-sequence,
      // calling setNodes/setEdges on its own timer, and a user-triggered
      // undo/delete here would fight those calls (undo could restore the
      // pre-reveal checkpoint just before the sequencer's next tick
      // overwrites it again; delete could remove a node the sequencer is
      // about to reference).
      if (
        isReadOnly ||
        isDrafting ||
        isCustomizePanelOpen ||
        isHistoryMode ||
        planReveal.isRevealing
      ) {
        return;
      }

      // Don't intercept when focused inside an input/textarea
      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLTextAreaElement
      ) {
        return;
      }

      if (isRedoKeydownEvent(event)) {
        event.preventDefault();
        handleRedo();
      } else if (isUndoKeydownEvent(event)) {
        event.preventDefault();
        handleUndo();
      } else if (event.key === 'Delete' || event.key === 'Backspace') {
        // Only swallow the key if we actually deleted something — otherwise
        // pressing Backspace with nothing selected would silently block other
        // shortcuts that legitimately want it.
        if (handleDeleteSelectedNodes()) {
          event.preventDefault();
        }
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [
    isReadOnly,
    isDrafting,
    isCustomizePanelOpen,
    isHistoryMode,
    planReveal.isRevealing,
    handleUndo,
    handleRedo,
    handleDeleteSelectedNodes,
  ]);

  /**
   * Update the data of a single LBNode in the graph.
   */
  const updateLBNodeData = useCallback(
    (nodeId: string, updates: Readonly<Partial<LBNodeData>>) => {
      if (isDrafting) return;
      setNodes((nodes) =>
        nodes.map((node) => {
          if (node.id !== nodeId) {
            return node;
          }
          // Freeze the merged data to catch in-place mutation.
          const merged = Object.freeze({ ...node.data, ...updates });
          return { ...node, data: merged as LBNodeDataWithIndexer };
        })
      );
    },
    [isDrafting]
  );

  /** Opens the customize panel for a specific node.
   *
   * The panel opens immediately. For conditional and filterControlFlow nodes,
   * getFilterControlFlowFields is called every time — the result is stored in
   * panelPrefetchedFields and passed directly as a prop to the panel so
   * nothing is ever saved inside graph node data.
   *
   * parent_id is derived from the live graph edges, so no metadata needs to
   * be persisted on the node itself.
   */
  const openCustomizePanelForNode = useCallback(
    async (nodeId: string) => {
      // Reset fields from any previous panel session, open the panel.
      setPanelPrefetchedFields(undefined);
      setPanelParentId(undefined);
      setNodePickerParentNodeId(null);
      setCustomizePanelNodeId(nodeId);
      setIsCustomizePanelOpen(true);
      // Determine whether this node type needs a fields fetch.
      const targetNode = nodesRef.current.find((n) => n.id === nodeId);
      if (!targetNode) return;

      const nodeInfo = getLBNodeInfo(targetNode.data);
      const isConditional = nodeInfo?.protobufNodeDataKey === 'conditional';
      const isFilterControlFlow =
        nodeInfo?.protobufNodeDataKey === 'filter_control_flow';
      if (!isConditional && !isFilterControlFlow) return;

      const currentEdges = edgesRef.current;
      const currentNodes = nodesRef.current;

      // Walk up the graph via either a directed edge OR a subgraph parentId.
      // filterControlFlow nodes use OutputType.SUBGRAPH_PARENT, which means
      // their children are linked via node.parentId rather than an edge.
      const resolveParentId = (childId: string): string | undefined =>
        currentEdges.find((e) => e.target === childId)?.source ??
        currentNodes.find((n) => n.id === childId)?.parentId;
      const directParentId = resolveParentId(nodeId);
      if (!directParentId) return;

      let parentId: string;
      if (isConditional) {
        const grandparentId = resolveParentId(directParentId);
        if (!grandparentId) return;
        parentId = grandparentId;
      } else {
        parentId = directParentId;
      }

      if (!currentNodes.find((n) => n.id === parentId)) return;
      setPanelParentId(parentId);
      // Fetch fields ONLY for FilterControlFlow nodes
      if (isFilterControlFlow && token) {
        try {
          const result = await getFilterControlFlowFields(token, {});
          // API returns FilterControlFlowField[] for filter nodes
          if (Array.isArray(result)) {
            setPanelPrefetchedFields(result as FilterControlFlowField[]);
          }
        } catch (error) {
          console.error('Failed to fetch fields for FilterControlFlow:', error);
          // Continue opening panel even if fetch fails
        }
      }
    },
    [token]
  );

  const handleOpenCustomizePanel = useCallback(
    async (nodeId: string) => {
      // Only history mode (viewing a past run) disables opening the panel
      // at all. Editing is still blocked while an AI Builder plan is
      // pending review (see customizePanelReadOnly below), but inspecting a
      // node stays available. Also disabled mid-reveal -- usePlanReveal is
      // still calling setNodes/setEdges on its own timer, and opening the
      // panel on a node that's about to be reconfigured by a later step
      // would show a stale snapshot the sequencer immediately overwrites.
      if (isHistoryMode || isDrafting || planReveal.isRevealing) {
        return;
      }

      // Clicking the node that's already open is a no-op.
      if (isCustomizePanelOpen && customizePanelNodeId === nodeId) {
        return;
      }

      // Clicking a DIFFERENT node while a panel is open asks that panel to
      // close first — CustomizeLBNodePanel's own "Save changes?" modal
      // fires if its draft is dirty, and openCustomizePanelForNode(nodeId)
      // only actually runs once that's resolved (Save or Don't Save), never
      // on a bare dismiss. Panel is keyed by customizePanelNodeId, so once
      // it does run, React remounts a fresh panel for the new node.
      if (isCustomizePanelOpen && customizePanelRef.current) {
        customizePanelRef.current.requestClose(() => {
          const _ = openCustomizePanelForNode(nodeId);
        });
        return;
      }

      await openCustomizePanelForNode(nodeId);
    },
    [
      isHistoryMode,
      isDrafting,
      planReveal.isRevealing,
      isCustomizePanelOpen,
      customizePanelNodeId,
      openCustomizePanelForNode,
    ]
  );

  // Initial node data for the panel. Read once at open time from the live
  // nodes[] — the panel copies this into a draft and tracks edits locally.
  const customizePanelInitialNodeData = useMemo(() => {
    if (!customizePanelNodeId) {
      return null;
    }
    const node = nodes.find((n) => n.id === customizePanelNodeId);
    return node ? (node.data as LBNodeData) : null;
  }, [customizePanelNodeId, nodes]);

  // The AI Agent node mirrored beneath a Transfer To node is read-only — the
  // agent is chosen on the transfer node, this is just a display copy.
  const customizePanelReadOnly =
    effectiveReadOnly ||
    customizePanelInitialNodeData?.ai_call_action?.is_transfer_escalation ===
      true;

  // The panel slides out when it closes, so it has to stay on the page for that: closing clears the
  // node it shows at once, which would take it off the page before it moved. What it showed is
  // kept for the length of the exit.
  const keepPanelForExit = useHoldWhileClosing(isCustomizePanelOpen);
  const lastPanelRef = useRef<{
    nodeId: string;
    data: LBNodeData;
    readOnly: boolean;
    prefetchedFields: FilterControlFlowField[] | undefined;
    parentId: string | undefined;
  } | null>(null);
  useEffect(() => {
    if (customizePanelNodeId && customizePanelInitialNodeData) {
      lastPanelRef.current = {
        nodeId: customizePanelNodeId,
        data: customizePanelInitialNodeData,
        readOnly: customizePanelReadOnly,
        prefetchedFields: panelPrefetchedFields,
        parentId: panelParentId,
      };
    }
  });
  const shownPanel =
    customizePanelNodeId && customizePanelInitialNodeData
      ? {
          nodeId: customizePanelNodeId,
          data: customizePanelInitialNodeData,
          readOnly: customizePanelReadOnly,
          prefetchedFields: panelPrefetchedFields,
          parentId: panelParentId,
        }
      : keepPanelForExit
        ? lastPanelRef.current
        : null;

  /**
   * Commit the panel's draft back to the graph and close the panel.
   * Saves a single undo checkpoint so Ctrl+Z rewinds the whole edit session.
   */
  const handleSaveAndCloseCustomizePanel = useCallback(
    (updatedData: LBNodeData) => {
      if (!customizePanelNodeId || isDrafting) {
        return;
      }
      const nodeId = customizePanelNodeId;
      saveCheckpoint(nodes, edges);

      const withData = nodes.map((node) =>
        node.id === nodeId
          ? {
              ...node,
              data: Object.freeze({
                ...node.data,
                ...updatedData,
              }) as LBNodeDataWithIndexer,
            }
          : node
      );

      // Transfer To nodes keep a materialized AI Agent child in sync with their
      // "Connect to an agent" fallback. Reconcile it after committing the edit.
      if (!updatedData.transfer_to_action) {
        setNodes(withData);
        handleCloseCustomizePanel();
        return;
      }

      const reconciled = reconcileTransferEscalationChild(
        nodeId,
        withData,
        edges
      );
      if (reconciled.structuralChange) {
        setNodes(calculateGraphLayout(reconciled.nodes, reconciled.edges));
        setEdges(reconciled.edges);
      } else {
        setNodes(reconciled.nodes);
      }
      handleCloseCustomizePanel();
    },
    [
      customizePanelNodeId,
      isDrafting,
      nodes,
      edges,
      saveCheckpoint,
      handleCloseCustomizePanel,
    ]
  );

  /**
   * Initialize the canvas with a trigger node of the given type.
   * Called when user clicks a trigger option in the empty state.
   */
  const handleCreateInitialTriggerNode = useCallback(
    (nodeType: NodeType) => {
      if (isDrafting) return;
      saveCheckpoint(nodes, edges);

      const nodeInfo = getNodeInfoForType(nodeType);
      const newNode = createNode(nodeInfo.createDefaultNodeData());
      // Root node is always at (0, 0)
      setNodes([{ ...newNode, position: { x: 0, y: 0 } }]);
      // Center and zoom the view to fit the new node
      fitViewRef.current?.();

      if (nodeInfo.showCustomizePanelOnCreation) {
        const _ = handleOpenCustomizePanel(newNode.id);
      }
    },
    [isDrafting, nodes, edges, saveCheckpoint, handleOpenCustomizePanel]
  );

  /**
   * Handle click on the add child button.
   * Opens the NodePicker for the clicked node.
   * Blocked until the workflow has been persisted and has a real ID.
   */
  const handleAddChildClick = useCallback(
    (nodeId: string, buttonRect: DOMRect) => {
      if (isDrafting) return;
      // Workflow has not been saved yet — wait for auto-persist to assign an ID.
      if (!workflow.id) {
        message.warning('Please wait while the workflow is being saved…');
        return;
      }
      // Customize panel has focus; ignore add-child until the user closes it.
      if (isCustomizePanelOpen) {
        return;
      }
      // A Transfer To node's escalation mirror is a read-only display copy of
      // its own agent selection — no node can be inserted between them.
      if (findTransferEscalationChild(nodeId, nodes, edges)) {
        return;
      }
      setNodePickerParentNodeId(nodeId);
      setNodePickerButtonRect(buttonRect);
    },
    [isDrafting, isCustomizePanelOpen, workflow.id, nodes, edges]
  );

  const handleCloseNodePicker = useCallback(() => {
    setNodePickerParentNodeId(null);
  }, []);

  const handleNodePickerChildSelected = useCallback(
    (childType: NodeType) => {
      if (isDrafting || !nodePickerParentNodeId) return;

      const parentNode = nodes.find((n) => n.id === nodePickerParentNodeId);
      if (!parentNode) return;

      const result = insertNewChild(parentNode, childType, nodes, edges);
      if (!result) return;

      saveCheckpoint(nodes, edges);
      setNodes(calculateGraphLayout(result.nodes, result.edges));
      setEdges(result.edges);
      handleCloseNodePicker();

      if (getNodeInfoForType(childType).showCustomizePanelOnCreation) {
        const _ = handleOpenCustomizePanel(result.childNode.id);
      }
    },
    [
      nodePickerParentNodeId,
      isDrafting,
      nodes,
      edges,
      handleCloseNodePicker,
      saveCheckpoint,
      handleOpenCustomizePanel,
    ]
  );

  const handleDeleteNode = useCallback(
    (nodeId: string) => {
      if (isReadOnly || isHistoryMode || isDrafting) {
        return;
      }
      // Coupled to its Transfer To node — can't be deleted on its own.
      const node = nodes.find((n) => n.id === nodeId);
      if (node && isTransferEscalationMirror(node)) {
        return;
      }
      saveCheckpoint(nodes, edges);
      const result = deleteNodes([nodeId], nodes, edges);
      setNodes(calculateGraphLayout(result.nodes, result.edges));
      setEdges(result.edges);
      if (customizePanelNodeId === nodeId) {
        handleCloseCustomizePanel();
      }
    },
    [
      isReadOnly,
      isHistoryMode,
      isDrafting,
      nodes,
      edges,
      saveCheckpoint,
      customizePanelNodeId,
      handleCloseCustomizePanel,
    ]
  );

  const handleDeleteCustomizePanelNode = useCallback(
    (nodeId: string) => {
      if (isReadOnly || isHistoryMode || isDrafting) {
        return;
      }
      // Coupled to its Transfer To node — can't be deleted on its own.
      const node = nodes.find((n) => n.id === nodeId);
      if (node && isTransferEscalationMirror(node)) {
        return;
      }
      saveCheckpoint(nodes, edges);
      const result = deleteNodes([nodeId], nodes, edges);
      setNodes(calculateGraphLayout(result.nodes, result.edges));
      setEdges(result.edges);
      if (customizePanelNodeId === nodeId) {
        handleCloseCustomizePanel();
      }
    },
    [
      isReadOnly,
      isHistoryMode,
      isDrafting,
      nodes,
      edges,
      saveCheckpoint,
      customizePanelNodeId,
      handleCloseCustomizePanel,
    ]
  );

  const isNodePickerOpen = nodePickerParentNodeId != null;

  const hasValidationErrors = presentedValidationErrors.size > 0;

  return (
    <LBNodeUpdateProvider
      value={{
        updateLBNodeData,
        onOpenCustomizePanel: handleOpenCustomizePanel,
        onOpenRunNodeDetail: handleOpenRunNodeDetail,
        onAddChildClick: handleAddChildClick,
        onDeleteNode: handleDeleteNode,
        isReadOnly: effectiveReadOnly,
        validationErrors: presentedValidationErrors,
        runNodeStatuses,
        workflowId: workflow.id,
        ownerEmail: workflow.owner_email,
      }}
    >
      <div
        // eslint-disable-next-line react/forbid-dom-props -- The height comes from CANVAS_HEIGHT in styles.ts.
        style={{
          display: 'flex',
          height: CANVAS_HEIGHT,
          //marginBottom: CANVAS_MARGIN_BOTTOM,
          //width: '100%',
          margin: '-28px -36px',
        }}
      >
        <div className={canvasStyles.canvasArea}>
          <LBCanvasControls
            workflow={workflow}
            onBack={onBack}
            onChangeWorkflowName={onChangeWorkflowName}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            isCopilotOpen={workflow.is_draft && isAgentPanelOpen}
            autoPersistStatus={autoPersistStatus}
            autoPersistLastSavedAt={autoPersistLastSavedAt}
            onPublishWorkflow={(id) => {
              if (!isDrafting) onPublishWorkflow?.(id, nodes, edges);
            }}
            onUnpublishWorkflow={onUnpublishWorkflow}
            onDeleteWorkflow={onDeleteWorkflow}
            canUndo={
              canUndo &&
              !effectiveReadOnly &&
              !isCustomizePanelOpen &&
              !isHistoryMode
            }
            canRedo={
              canRedo &&
              !effectiveReadOnly &&
              !isCustomizePanelOpen &&
              !isHistoryMode
            }
            onUndo={handleUndo}
            onRedo={handleRedo}
            hasValidationErrors={hasValidationErrors}
            unresolvedFieldCount={unresolvedFieldSummary.unresolvedFieldCount}
            isDrafting={isDrafting}
            onTest={
              isTestRunsEnabled && canUpdateAutomations
                ? handleOpenTestRunPanel
                : undefined
            }
          />
          {isDrafting && (
            <div
              role='status'
              aria-live='polite'
              // eslint-disable-next-line react/forbid-dom-props -- The position comes from RUN_BANNER_TOP in styles.ts.
              style={{
                position: 'absolute',
                top: RUN_BANNER_TOP + 12,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 12,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '6px 12px',
                borderRadius: 999,
                background: '#111111',
                color: '#ffffff',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <Lock size={13} /> Canvas locked while Copilot is drafting
            </div>
          )}
          <CopilotButton
            isDraft={workflow.is_draft}
            activeTab={activeTab}
            isOpen={isAgentPanelOpen}
            disabled={!workflow.id}
            recoveryAvailable={hasCopilotRecovery}
            onClick={() => setIsAgentPanelOpen((open) => !open)}
          />
          <ReactFlow
            nodes={revealNodes}
            edges={revealEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            // Disable ReactFlow's built-in Delete/Backspace handler — it only
            // removes the selected node (no descendants) and bypasses our
            // undo checkpoint. Delete is handled by the global document
            // listener above instead.
            deleteKeyCode={null}
            nodeTypes={{ lbNode: LBNodeDisplay }}
            edgeTypes={{ lbEdge: LBEdgeDisplay }}
            nodeOrigin={NODE_ORIGIN}
            defaultEdgeOptions={{
              type: 'lbEdge',
              markerEnd: LBEDGE_MARKER_END,
            }}
            nodesConnectable={false}
            nodesDraggable={false}
            edgesFocusable={false}
            // Disable pan-on-drag when the NodePicker is open, so that
            // we can handle clicks on the canvas. If panOnDrag is enabled,
            // the canvas swallows mouse-down events before NodePicker's
            // MouseEvent handler is able to.
            panOnDrag={!isNodePickerOpen}
            // Disable zooming when the NodePicker is open
            zoomOnScroll={!isNodePickerOpen}
            zoomOnPinch={!isNodePickerOpen}
            zoomOnDoubleClick={!isNodePickerOpen}
            proOptions={{ hideAttribution: true }}
            // React Flow queues a fit when the first measurable reveal nodes
            // mount. During a Copilot reveal that partial-graph fit would
            // overwrite usePlanReveal's completed-footprint camera pre-roll
            // and zoom back into the first few nodes. Keep the pre-roll stable
            // until the reveal finishes, then restore the normal cleanup fit.
            fitView={shouldUseReactFlowAutoFit(planReveal.isCameraFramed)}
            fitViewOptions={{
              maxZoom: DEFAULT_ZOOM_LEVEL,
              padding: FIT_VIEW_PADDING,
            }}
            data-testid='react-flow'
          >
            <Background />
            <FitViewRefSetter
              fitViewRef={fitViewRef}
              fitBoundsRef={fitBoundsRef}
            />
            {nodePickerParentNodeId && nodePickerButtonRect && (
              <NodePickerOverlay
                parentNodeId={nodePickerParentNodeId}
                buttonRect={nodePickerButtonRect}
                onClose={handleCloseNodePicker}
                onSelect={handleNodePickerChildSelected}
              />
            )}
          </ReactFlow>
          {isHistoryMode && showRunGraphLoading && (
            // eslint-disable-next-line react/forbid-dom-props -- Built from LOADING_STACK_STYLE and RUN_BANNER_Z_INDEX in styles.ts.
            <div style={RUN_LOADING_OVERLAY_STYLE}>
              <Spin size={LOADING_SPINNER_SIZE} />
              <p
                // eslint-disable-next-line react/forbid-dom-props -- LOADING_LABEL_STYLE in styles.ts styles every Logiblocks loading label.
                style={LOADING_LABEL_STYLE}
              >
                Loading...
              </p>
            </div>
          )}
          {isHistoryMode && runDetailError !== null && (
            <Alert
              type='error'
              showIcon
              message={runDetailError}
              style={RUN_BANNER_STYLE}
            />
          )}
          {isHistoryMode &&
            (runNotice === 'missing' || runNotice === 'unavailable') && (
              <Alert
                type='warning'
                showIcon
                message='This run used an older version of the workflow that is no longer available and cannot be shown.'
                style={RUN_NOTICE_BANNER_STYLE}
              />
            )}
          {isHistoryMode && runNotice === 'load-failed' && (
            <Alert
              type='warning'
              showIcon
              message='The workflow for this run could not be loaded.'
              style={RUN_NOTICE_BANNER_STYLE}
            />
          )}
          {isHistoryMode && runNotice === 'older' && (
            <Alert
              type='info'
              showIcon
              message='You are viewing an older version of this workflow.'
              style={RUN_NOTICE_BANNER_STYLE}
            />
          )}
          {isHistoryMode && (
            <WorkflowRunHistoryPanel
              key={runHistoryPanelKey}
              workflowId={workflow.id}
              selectedRunId={selectedRunId}
              runStatusChangeCount={runStatusChangeCount}
              onSelectRunId={setSelectedRunId}
              onRefreshRunDetail={refetchRunDetail}
            />
          )}
          {isHistoryMode && (
            <RunNodeAttemptsDrawer
              nodeId={runNodeDetailId}
              nodeType={runNodeDetailType}
              rows={runNodeDetailRows}
              conditional={runNodeDetailConditional}
              open={runNodeDetailId !== null}
              onClose={() => setRunNodeDetailId(null)}
            />
          )}
          {nodes.length === 0 &&
            !isReadOnly &&
            !isAgentPanelOpen &&
            (isCopilotEnabled && !showTriggerPicker ? (
              <div className={canvasStyles.emptyStateOverlay}>
                <AutomationsEmptyState
                  variant='canvas'
                  onGenerate={handleCanvasGenerate}
                  onBuildFromScratch={() => setShowTriggerPicker(true)}
                />
              </div>
            ) : (
              <div className={canvasStyles.emptyStateOverlay}>
                <InitialTriggerSelector
                  onSelectTrigger={handleCreateInitialTriggerNode}
                />
              </div>
            ))}
          {shownPanel && (
            <CustomizeLBNodePanel
              key={shownPanel.nodeId}
              ref={customizePanelRef}
              open={isCustomizePanelOpen}
              onSaveAndClose={handleSaveAndCloseCustomizePanel}
              onCloseWithoutSave={handleCloseCustomizePanel}
              onDeleteNode={handleDeleteCustomizePanelNode}
              nodeId={shownPanel.nodeId}
              initialNodeData={shownPanel.data}
              isReadOnly={shownPanel.readOnly}
              graph={{ nodes, edges }}
              workflowId={workflow.id}
              prefetchedFields={shownPanel.prefetchedFields}
              prefetchedFieldsParentId={shownPanel.parentId}
            />
          )}
          {workflow.is_draft && (
            <WorkflowAgentChatPanel
              ref={agentPanelRef}
              open={isAgentPanelOpen}
              onClose={() => setIsAgentPanelOpen(false)}
              workflowId={workflow.id}
              workflowGraphRevision={workflow.graph_revision}
              nodes={nodes}
              edges={edges}
              durableDraftEnabled={isCopilotEnabled}
              onPersistSegment={flushWorkflowSave}
              onApplyPlan={handleApplyPlan}
              onReconcileSegmentGraph={handleReconcileSegmentGraph}
              onStartReveal={planReveal.start}
              isRevealing={planReveal.isRevealing}
              onCancelReveal={planReveal.cancel}
              onRestoreSnapshot={handleRestoreAgentSnapshot}
              onDraftLockChange={setDraftLockReason}
              onConfigurationLifecycleChange={
                ignoreConfigurationLifecycleChange
              }
              onOpenNodeConfiguration={(nodeId) => {
                const _ = handleOpenCustomizePanel(nodeId);
              }}
              initialMessage={canvasAgentPrompt ?? initialAgentPrompt}
            />
          )}
          {testRunPanel}
        </div>
      </div>
    </LBNodeUpdateProvider>
  );
}
